import React from 'react';
import { get, find } from 'lodash';

import { STOREFRONT_LIMIT_COUNT } from 'constants/storefront';
import { normalizeUrl } from 'utils';
import { expandString } from 'utils/string';

import Tooltip from 'components/tooltip';

const { APP_DEPLOYMENT_PUBLIC_URL, CLIENT_URL } = process.env;

// Scheme an app uses to point at its own storefront frontend
const FRONTEND_LINK_PREFIX = 'frontend://';

const THEME_LINKS = {
  origin: 'https://github.com/swellstores/origin-theme/',
  horizon: 'https://github.com/swellstores/horizon',
};

/**
 * The storefront's preview host. Unlike getStorefrontURL's `preview` flag, which
 * yields to a primary storefront's verified domain, this is always the preview
 * subdomain -- the only host that runs the storefront in preview mode.
 * @param {object} storefront
 * @returns {string}
 */
export function getStorefrontPreviewURL({ client_id, id }) {
  return CLIENT_URL.replace('CLIENT_ID', `${client_id}--${id}--preview`);
}

export function getStorefrontURL(storefront, preview = false, editor = false) {
  if (!storefront) return '';

  const {
    id,
    slug,
    primary,
    domains,
    external,
    hosted,
    app_id,
    client_id,
    environment_id,
  } = storefront;

  // External storefronts
  if (!app_id && external?.custom && external?.custom_url) {
    return external.custom_url.replace(/\/+$/, '');
  }

  // Primary with domain
  if (primary && !preview && !editor) {
    const primaryDomain = find(domains?.results, {
      primary: true,
      verified: true,
    });
    if (primaryDomain) {
      return normalizeUrl(primaryDomain.name, {
        defaultProtocol: 'https://',
      });
    }
  }

  // V1 hosted storefronts
  if (hosted) {
    return CLIENT_URL.replace('CLIENT_ID', slug);
  }

  if (editor) {
    const suffix = storefront.theme_id ? 'dev' : 'local';
    return CLIENT_URL.replace('CLIENT_ID', `${client_id}--${id}--${suffix}`);
  } else if (preview) {
    return getStorefrontPreviewURL(storefront);
  } else if (!primary || environment_id) {
    return CLIENT_URL.replace('CLIENT_ID', `${client_id}--${id}`);
  }

  return CLIENT_URL.replace('CLIENT_ID', client_id);
}

export function getLocalProxyStorefrontURL(session, editor = false) {
  const { id, client_id, local_proxy } = session;

  if (local_proxy) {
    if (editor) {
      return CLIENT_URL.replace('CLIENT_ID', `${client_id}--${id}--local--dev`);
    } else {
      return CLIENT_URL.replace('CLIENT_ID', `${client_id}--${id}--local`);
    }
  }

  return '';
}

export function storefrontPublicDeploymentURL(storefront) {
  const { preview_deployment_name } = storefront;
  return APP_DEPLOYMENT_PUBLIC_URL.replace(
    'DEPLOYMENT_ID',
    preview_deployment_name,
  );
}

export function storefrontCheckoutURL(clientId, dev = false) {
  return CLIENT_URL.replace('CLIENT_ID', `${clientId}${dev ? '--dev' : ''}`);
}

export function renderStorefrontV1ThemeDesc(storefront) {
  return (
    <>
      <a
        href={THEME_LINKS[storefront.source_id]}
        target="_blank"
        rel="noreferrer"
      >
        {storefront.source?.name || storefront.source_id} theme
      </a>{' '}
      v{storefront.source_version}
    </>
  );
}

/**
 * Returns the storefront link for a given collection and record.
 * @param {string} collection
 * @param {object} record
 * @param {object} storefront
 * @returns {string}
 */
export function storefrontCollectionLink(collection, record, storefront) {
  const storefrontUrl = getStorefrontURL(storefront);

  let values = record;

  // Default pattern
  let pattern = `/${collection}/:slug`;

  // Find the page config if defined by a storefront app
  const storefrontThemeConfig = storefront?.app?.storefront?.theme;
  if (storefrontThemeConfig) {
    const storefrontAppCollectionTemplate =
      storefrontThemeConfig?.template_collections?.[collection];
    const storefrontThemePageConfig = find(storefrontThemeConfig?.pages, {
      id: storefrontAppCollectionTemplate,
    });

    if (storefrontThemePageConfig?.url) {
      pattern = storefrontThemePageConfig.url;
    }
  }

  try {
    const url = pattern
      .replace(/:([\w.]+)/g, (match) => {
        const key = match.slice(1);
        const value = get(values, key);
        if (value) {
          return value;
        }
        return '';
      })
      .replace(/\/\//g, '/');

    return `${storefrontUrl}${url}`;
  } catch {
    return `${storefrontUrl}/${collection}/${values?.slug || ''}`;
  }
}

/**
 * Removes ? and # from the url
 * @param {string} href
 * @returns {string}
 */
export function removeQueryFromUrl(href) {
  return href.split('?')[0].split('#')[0];
}

/**
 * Matches a URL against a list of pages and returns page info.
 * @param {string} href
 * @param {Array} pages
 * @returns {{ pageId?: string, recordId?: string }}
 */
export function matchPageRoute(href, pages) {
  const url = removeQueryFromUrl(href);

  for (const page of pages) {
    // special situation with url /blogs/blog_category/blog_slug
    if (page.url.includes(':category.slug') && page.url.includes(':slug')) {
      const basePageUrl = page.url
        .replace('/:slug', '')
        .replace('/:category.slug', '');
      const regex = new RegExp(`^${basePageUrl}/([^/]+)/([^/]+)$`);
      const match = url.match(regex);

      if (match && match.length > 2) {
        return { pageId: page.id, recordId: match[2] };
      }
    }

    if (page.url.includes(':slug') || page.url.includes(':id')) {
      const basePageUrl = page.url.replace('/:slug', '').replace('/:id', '');
      const regex = new RegExp(`^${basePageUrl}/([^/]+)$`);
      const match = url.match(regex);

      if (match) {
        return { pageId: page.id, recordId: match[1] };
      }
    } else if (page.url === url) {
      return { pageId: page.id };
    }
  }

  return {};
}

/**
 * Get a corresponding page url
 * @param {Array} pages
 * @param {string} pageId
 * @param {string} recordId
 * @returns {string}
 */
export function getPageRoute(pages, pageId, recordId = '') {
  for (const page of pages) {
    if (page.id === pageId && page.url) {
      const replaced = page.url
        .replace(':slug', recordId)
        .replace(':id', recordId);

      return replaced;
    }
  }

  return '';
}

/**
 * Checks if the storefront limit has been reached.
 * Returns the limit if reached, false otherwise.
 * @param {object} storefront
 * @returns {number|boolean}
 */
export function storefrontLimitReached(storefronts) {
  return storefronts?.count >= STOREFRONT_LIMIT_COUNT
    ? STOREFRONT_LIMIT_COUNT
    : false;
}

/**
 * Render element with tooltip indicating storefront limit.
 * @param {object} storefronts
 * @param {function} render
 * @param {object} tooltipProps
 * @returns {React.ReactNode}
 */
export function renderWithStorefrontLimitTooltip(
  storefronts,
  render,
  tooltipProps,
) {
  if (storefrontLimitReached(storefronts)) {
    return (
      <Tooltip
        message={`You've reached the limit of ${STOREFRONT_LIMIT_COUNT} storefronts.`}
        {...tooltipProps}
      >
        {render(true)}
      </Tooltip>
    );
  }

  return render(false);
}

/**
 * Returns the options for the storefront theme template select field.
 * @param {string[]} templates
 * @returns {object[]}
 */
export function storefrontThemeTemplateSelectOptions(templates = []) {
  const options = [
    {
      value: '',
      label: 'Default template',
    },
  ];

  for (const templateId of templates) {
    options.push({
      value: templateId,
      label: templateId,
    });
  }

  return options;
}

/**
 * Renders the theme template selector field for a given collection.
 * @param {string} collection
 * @param {object} themeTemplates
 * @param {function} handler
 * @returns {React.ReactNode|null}
 */
export function renderThemeTemplateSelector(
  collection,
  themeTemplates,
  handler,
) {
  if (!themeTemplates?.[collection]) {
    return null;
  }

  const options = storefrontThemeTemplateSelectOptions(
    themeTemplates[collection].list,
  );

  return handler({
    label: 'Theme template',
    help: 'Template to display this product in your storefront theme',
    options,
  });
}

/**
 * Opens checkout in the editor
 * @param {string} checkoutHref
 */
export function redirectToCheckout(checkoutHref) {
  // checkoutId is a last url part
  const parts = removeQueryFromUrl(checkoutHref).split('/');
  const checkoutId = parts[parts.length - 1];
  const url = removeQueryFromUrl(window.parent.location.href);

  // open checkout in the editor, provide checkoutId as a parameter
  window.parent.location.href = `${url}?section=checkout&checkoutId=${checkoutId}`;
}

/**
 * The URL of an app-defined editor for a storefront, or null if the app has
 * none. `frontend://` is the storefront's own frontend, not the app pages host:
 * the editor renders the storefront being edited, so it needs that storefront's
 * preview context to run.
 *
 * @param {object} storefront
 * @param {object} [storefrontApp] installed app record, from getInstalledApps()
 * @returns {string | null}
 */
export function getStorefrontAppEditorURL(storefront, storefrontApp) {
  const link = storefrontApp?.app?.storefront?.editor?.link;

  // Only a frontend:// link is embedded; an http(s) one is the app's own
  // domain, opened in a new tab by getStorefrontEditorAction.
  if (
    !storefront ||
    storefront.hosted ||
    !link?.startsWith(FRONTEND_LINK_PREFIX)
  ) {
    return null;
  }

  // expandString returns null when the pattern references a field the
  // storefront doesn't have.
  const path = expandString(
    link.replace(FRONTEND_LINK_PREFIX, '/'),
    storefront,
  );

  if (!path) {
    return null;
  }

  // Opened through FrontendFrame, which adds a one-time access code, so the
  // app can tell a merchant in the dashboard from any visitor of the public
  // preview host
  return new URL(path, getStorefrontPreviewURL(storefront)).toString();
}

/**
 * Returns the editor action for a storefront, or null if it has none.
 *
 * A storefront app may define its own editor in swell.json (`storefront.editor`),
 * in which case it replaces the standard theme editor. Otherwise the standard
 * editor is offered whenever the `/storefronts/:id/editor` route can render it
 * (see containers/StorefrontEditor).
 *
 * @param {object} storefront
 * @param {object} [storefrontApp] installed app record, from getInstalledApps()
 * @returns {{ label: string, to?: string, href?: string, newTab?: boolean } | null}
 */
export function getStorefrontEditorAction(storefront, storefrontApp) {
  if (!storefront || storefront.hosted) {
    return null;
  }

  const editor = storefrontApp?.app?.storefront?.editor;

  if (editor?.link) {
    const label = editor.label || 'Open editor';

    // expandString aborts and returns null when the pattern references a field
    // the storefront doesn't have, in which case we show no editor action.
    if (!editor.link.startsWith(FRONTEND_LINK_PREFIX)) {
      const href = expandString(editor.link, storefront);

      return href ? { label, href } : null;
    }

    // An embedded editor is rendered by the storefront editor route, the same
    // place the standard theme editor lives -- but in a new tab, so the
    // dashboard the merchant came from is still there behind it.
    return getStorefrontAppEditorURL(storefront, storefrontApp)
      ? { label, to: `/storefronts/${storefront.id}/editor`, newTab: true }
      : null;
  }

  if (storefront.theme_id || storefront.ai_session_id) {
    return {
      label: 'Edit theme',
      to: `/storefronts/${storefront.id}/editor`,
    };
  }

  return null;
}
